import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { TextButton, Button } from '@bibliocommons/base-buttons';
import { ChevronDown, ChevronUp, Close, Ellipsis, EllipsisVertical } from '@bibliocommons/base-icons';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';

import './SystemMessages.scss';

const COLLAPSED_MAX_HEIGHT_PX = 24;

const SystemMessages = ({
  dismissMessage,
  isDismissible,
  messages,
  systemMessageBackgroundColor,
  systemMessageTextColor
}) => {
  const [expandedMessageIds, setExpandedMessageIds] = useState([]);
  const [expandableMessageIds, setExpandableMessageIds] = useState([]);
  const messageRefs = useRef({});

  useEffect(() => {
    const determineIfExpandable = () => {
      const newExpandableIds = messages
        .map(message => {
          const { id } = message;
          const messageContainer = messageRefs.current[id];
          const messageHeight = messageContainer ? messageContainer.scrollHeight : 0;

          return messageHeight > COLLAPSED_MAX_HEIGHT_PX ? id : null;
        })
        .filter(Boolean);

      setExpandableMessageIds(newExpandableIds);
    };

    const onWindowResize = debounce(determineIfExpandable, 100);
    const expandabilityCheckTimeoutId = setTimeout(determineIfExpandable, 0);

    window.addEventListener('resize', onWindowResize);

    return () => {
      // Both of these outlive the component unless cancelled explicitly, and either one calls
      // setExpandableMessageIds against an unmounted tree: the timer is not tied to the listener at
      // all, and removing the listener does not cancel an invocation lodash has already queued
      // (that is what .cancel() is for). React then warns about a state update on an unmounted
      // component, which the console.error-throwing test harness turns into a failure attributed to
      // whichever unrelated test happens to be running. See CORE-78819.
      //
      // Note this cleanup also runs between renders, not only on unmount: the effect is keyed on
      // [messages], so dismissing a message tears down and reschedules both of these.
      clearTimeout(expandabilityCheckTimeoutId);
      onWindowResize.cancel();
      window.removeEventListener('resize', onWindowResize);
    };
  }, [messages]);

  const numTotalMessages = messages.length;
  if (numTotalMessages <= 0) {
    return null;
  }

  const handleDismissMessage = index => {
    const dismissedMessage = messages[index];
    const { id: dismissedMessageId } = dismissedMessage;

    const nextMessage = messages[index + 1] ?? null;
    const { id: nextMessageId } = nextMessage || {};

    const nextMessageEl = messageRefs.current[nextMessageId];

    if (nextMessageEl) {
      nextMessageEl.focus();
    } else {
      document.querySelector('main')?.focus();
    }

    dismissMessage(dismissedMessageId);
  };

  const handleToggleClick = id => {
    setExpandedMessageIds(prevIds => (prevIds.includes(id) ? prevIds.filter(msgId => msgId !== id) : [...prevIds, id]));
  };

  return (
    <ul className="cp-system-messages">
      {messages.map((message, index) => {
        const { id, message: messageText } = message;
        if (!messageText) {
          return null;
        }

        const isExpanded = expandedMessageIds.includes(id);
        const isExpandable = expandableMessageIds.includes(id);
        const uniqueIdPrefix = `system-message-${id}`;
        const messageNumber = index + 1;

        return (
          <li
            key={id}
            className={`system-message ${isExpanded ? 'system-message--expanded' : ''}`}
            style={{ backgroundColor: systemMessageBackgroundColor }}
          >
            <div className="message-container">
              <div
                className={`system-message__content ${isExpanded ? 'system-message__content--expanded' : ''}`}
                id={`${uniqueIdPrefix}_Expandable`}
                ref={el => (messageRefs.current[id] = el)}
                style={{ maxHeight: isExpanded ? 'none' : `${COLLAPSED_MAX_HEIGHT_PX}px` }}
                tabIndex={-1}
              >
                {/* eslint-disable react/no-danger */}
                <div
                  className="message-text"
                  style={{ color: systemMessageTextColor }}
                  dangerouslySetInnerHTML={{ __html: messageText }}
                />
                {/* eslint-enable react/no-danger */}
              </div>

              {isExpandable && (
                <TextButton
                  aria-hidden="true"
                  tabIndex="-1"
                  aria-expanded={isExpanded}
                  aria-controls={`${uniqueIdPrefix}_Expandable`}
                  className="toggle"
                  dataKey="is-expanded-toggle"
                  handleClick={() => handleToggleClick(id)}
                  leftAligned
                  type="button"
                  style={{ color: systemMessageTextColor }}
                >
                  <div className="toggle-text">
                    <span className="toggle-text__label">
                      <FormattedMessage id={isExpanded ? 'read_less' : 'read_more'} />
                      {isExpanded ? <ChevronUp /> : <ChevronDown />}
                    </span>
                    <span className="toggle-text__icon">{isExpanded ? <EllipsisVertical /> : <Ellipsis />}</span>
                  </div>
                </TextButton>
              )}

              {isDismissible && (
                <Button
                  className="dismiss-button"
                  handleClick={() => handleDismissMessage(index)}
                  dataKey="dismiss-button"
                  style={{ color: systemMessageTextColor }}
                >
                  <ScreenReaderMessage>
                    <FormattedMessage id="close_message" values={{ messageNumber, numTotalMessages }} />
                  </ScreenReaderMessage>
                  <Close />
                </Button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

SystemMessages.defaultProps = {
  systemMessageBackgroundColor: null,
  systemMessageTextColor: null
};

SystemMessages.propTypes = {
  dismissMessage: PropTypes.func.isRequired,
  isDismissible: PropTypes.bool.isRequired,
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      message: PropTypes.string.isRequired
    })
  ).isRequired,
  systemMessageBackgroundColor: PropTypes.string,
  systemMessageTextColor: PropTypes.string
};

export default SystemMessages;
